import { ThumbsDownIcon, ThumbsUpIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { HARNESS_ACTION_THUMBS } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useMyFeedbackForSubject } from '@/api/rpc/hooks/core/user-feedback/useMyFeedbackForSubject';
import { useSubmitFeedback } from '@/api/rpc/hooks/core/user-feedback/useSubmitFeedback';
import { useDeleteFeedback } from '@/api/rpc/hooks/core/user-feedback/useDeleteFeedback';

/**
 * Thumbs up/down on an assistant action card. Persists as user feedback on
 * the tool call (same store as chat-message thumbs), so outcome analysis can
 * join it to the tool-call ledger.
 */
export function NamiActionThumbs({ toolCallId }: { toolCallId: string }) {
  const gt = useGT();
  const { feedbackByScoreName } = useMyFeedbackForSubject({
    subjectType: HARNESS_ACTION_THUMBS.subjectType,
    subjectId: toolCallId,
  });
  const submit = useSubmitFeedback();
  const remove = useDeleteFeedback();
  const existing =
    feedbackByScoreName?.[HARNESS_ACTION_THUMBS.scoreName] ?? null;
  const value = existing?.booleanValue ?? null;

  const choose = (next: boolean) => {
    if (value === next) {
      if (existing) {
        remove.mutate({
          feedbackId: existing.id,
          subjectType: HARNESS_ACTION_THUMBS.subjectType,
          subjectId: toolCallId,
        });
      }
      return;
    }
    submit.mutate({
      subjectType: HARNESS_ACTION_THUMBS.subjectType,
      subjectId: toolCallId,
      scoreName: HARNESS_ACTION_THUMBS.scoreName,
      value: { dataType: 'BOOLEAN', booleanValue: next },
    });
  };

  return (
    <div className="flex items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'h-6 w-6 text-muted-foreground hover:text-foreground',
          value === true && 'text-emerald-600 hover:text-emerald-700',
        )}
        aria-label={gt('Helpful', {
          context: 'thumbs up on an AI assistant action card',
        })}
        aria-pressed={value === true}
        onClick={() => choose(true)}
      >
        <ThumbsUpIcon
          className="size-3.5"
          weight={value === true ? 'fill' : 'regular'}
        />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn(
          'h-6 w-6 text-muted-foreground hover:text-foreground',
          value === false && 'text-rose-600 hover:text-rose-700',
        )}
        aria-label={gt('Not helpful', {
          context: 'thumbs down on an AI assistant action card',
        })}
        aria-pressed={value === false}
        onClick={() => choose(false)}
      >
        <ThumbsDownIcon
          className="size-3.5"
          weight={value === false ? 'fill' : 'regular'}
        />
      </Button>
    </div>
  );
}
